import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { getInvoiceApi, updatePartiesApi, getInvoiceRateItemsApi } from '../apiService';
import { globalGetService, globalPutService } from '../../../utils/globalApiServices';
import { checkApiStatus, normalizeTaxRate } from '../../../utils';
import { trackActivity } from '../../../utils/mixpanel';
import { PageLoader, titanAviationIntance } from '../../../shared_elements';
import { getStorage } from '../../../shared_elements/actionCreators';
import { getAdditionalTax, getTotalTax } from '..';
import AInvItems, { getInvoiceDialogWidthProps } from './AInvItems';

const isManagementFeeInvoice = (invoice) => invoice?.invoice_type?.value === 10;

const getInvoiceItemsType = (invoice) => {
    const label = invoice?.invoice_type?.label;
    if (label === 'Rental') return 'rentalInvoice';
    if (label === 'Reserve') return 'reserveInvoice';
    if (label === 'Penalty') return 'penaltyInvoice';
    if (label === 'Security Deposit') return 'securityDepositInvoice';
    if (label === 'Credit Note' || label === 'Debit Note') {
        return invoice?.parent_id ? 'linkedInvoice' : 'LeaseCreditDebitNote';
    }
    return null;
};


const EditInvoiceItemModal = ({ onToggleModal, getResponseBack }) => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const params = useParams();
    const [invoice, setInvoice] = useState({});
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState({});
    const [isDropDownLoading, setIsDropDownLoading] = useState({ storage: false })
    const [tableErrors, setTableErrors] = useState({});
    const [rateItems, setRateItems] = useState([]);
    const [rateItemsLoading, setRateItemsLoading] = useState(false);

    useEffect(() => {
        getInvoice();
        trackActivity('Page Viewed', { page_title: 'Edit Invoice' });
    }, []);
   
    const getInvoiceRateItems = (leaseSlug, invoiceTypeValue) => {
        setRateItemsLoading(true);
        globalGetService(getInvoiceRateItemsApi({ lease_slug: leaseSlug, invoice_type: invoiceTypeValue }))
            .then(response => {
                setRateItemsLoading(false);
                if (checkApiStatus(response)) {
                    setRateItems(response?.data?.data?.items || []);
                }
            });
    };
    
    const getInvoice = () => {
        setLoading(true);
        setIsDropDownLoading({ storage: true });
        globalGetService(getInvoiceApi(params))
            .then(response => {
                if (checkApiStatus(response)) {
                    const data = response.data.data;

                    if (data?.status?.value !== 1) {
                        navigate(`/invoices/view/${data.slug}`, { replace: true });
                        return;
                    }
                    dispatch(getStorage({ lease_slug: data?.lease_slug, module_name: "invoices", setIsDropDownLoading }));
                    setInvoice(data);
                    if (getInvoiceItemsType(data) === 'LeaseCreditDebitNote' && data?.linked_invoice_type?.value) {
                        getInvoiceRateItems(data.lease_slug, data.linked_invoice_type.value);
                    }
                }
                setLoading(false);
            });
    };

    const onFieldChange = (keyParams, value, objectType, index) => {
        if (objectType === 'invoice_items') {
            if (keyParams === 'sub_total' || keyParams === 'tax_rate') {
                setInvoice((prev) => {
                    let taxAmount = 0;
                    let subTotal = 0;
                    const existingTaxRate = parseFloat(prev.invoice_items[index].tax_rate) || 0;
                    if (keyParams === 'sub_total') {
                        subTotal = parseFloat(value) || 0;
                        if (existingTaxRate) {
                            taxAmount = subTotal * (existingTaxRate / 100);
                        }
                    } else {
                        subTotal = parseFloat(prev.invoice_items[index].sub_total) || 0;
                        if (value) {
                            taxAmount = subTotal * (parseFloat(value) / 100);
                        }
                    }
                    // Same "Additional Tax" (included-in-funds) percentage AInvItems' own Sub Total column sums —
                    // must be re-included here too, since sub_amount_total is rebuilt from scratch below.
                    const totalItemTax = (prev?.tax_details || [])
                        .filter(t => t?.is_included_in_funds)
                        .reduce((sum, t) => sum + (parseFloat(t?.percentage) || 0), 0);
                    const subAmountTotal = subTotal + taxAmount + (subTotal * totalItemTax / 100);
                    return {
                        ...prev,
                        invoice_items: prev.invoice_items.map((item, idx) =>
                            idx !== index ? item : { ...item, [keyParams]: value, tax_amount: taxAmount.toFixed(4), sub_amount_total: subAmountTotal.toFixed(4) }
                        ),
                    };
                });
            } else {
                setInvoice((prev) => ({
                    ...prev,
                    invoice_items: prev.invoice_items.map((item, idx) => idx !== index ? item : { ...item, [keyParams]: value }),
                }));
            }
        } else if (objectType) {
            setInvoice({
                ...invoice,
                [objectType]: {
                    ...invoice[objectType],
                    [keyParams]: value
                }
            })
        } else {
            setInvoice({ ...invoice, [keyParams]: value });
        }

    }

    const onAddRemoveInvoiceItem = (action, index) => {
        if (action === 'add') {
            setInvoice(prev => ({
                ...prev,
                invoice_items: [...(prev.invoice_items || []), { invoice_item_name: '', description: '', tax_rate: 0, tax_amount: '', sub_total: 0 }]
            }));
        } else {
            setInvoice(prev => ({
                ...prev,
                invoice_items: prev.invoice_items.filter((item, idx) => idx !== index)
            }));
        }
    }

    const resetTableError = (index, field) => {
        setTableErrors(prev => ({
            ...prev,
            [index]: { ...prev[index], [field]: '' }
        }));
    }

    const onEditInvoice = () => {
        let validationInputs = {
            invoice_number: invoice?.invoice_number?.trim()?.length ? '' : 'Please enter Invoice Number',
            date: invoice?.date ? '' : 'Please enter Invoice Date',
            due_date: invoice?.due_date ? '' : 'Please enter Invoice Due Date',
            ...(!(invoice?.parent_slug || [1, 2, 10].includes(invoice?.invoice_type?.value)) && {
                start_date: invoice?.start_date ? '' : 'Please enter Period Start Date',
                end_date: invoice?.end_date ? '' : 'Please enter Period End Date',
            }),
        }

        let newItemErrors = {};
        if (Array.isArray(invoice?.invoice_items)) {
            invoice.invoice_items.forEach((row, index) => {
                const errors = {};
                if (!row.invoice_item_name) {
                    errors.invoice_item_name = "Please enter Invoice Item";
                }
                if ((!titanAviationIntance || invoice?.invoice_type?.value === 9) && !row.description?.trim()) {
                    errors.description = "Please enter Description";
                }
                if ([4, 5, 9].includes(invoice?.invoice_type?.value) && !invoice?.parent_id) {
                    if (row.sub_total === '' || row.sub_total === null || row.sub_total === undefined) {
                        errors.sub_total = "Please enter Amount.";
                    } else if (parseFloat(row.sub_total) <= 0) {
                        errors.sub_total = "Please enter an Amount greater than 0.";
                    }
                }
                if (invoice?.parent_id && [5].includes(invoice?.invoice_type?.value)) {
                    if (Number(row.sub_total) > Number(row.original_amount)) {
                        errors.sub_total = "Amount can't exceed Original Amount";
                    }
                }
                if ([6].includes(invoice?.invoice_type?.value)) {
                    if (Number(row.sub_total) > Number(row.original_amount)) {
                        errors.sub_total = "Amount can't exceed Security Deposit Amount";
                    }
                }
                if (Object.keys(errors).length > 0) {
                    newItemErrors[index] = errors;
                }
            });
            setTableErrors(newItemErrors);
        }

        if (Object.keys(validationInputs).every((k) => { return validationInputs[k] === ""; }) && Object.keys(newItemErrors).length === 0) {
            setLoading(true);
            let payload = { ...invoice, currency: invoice?.currency?.code };
            if (Array.isArray(invoice?.invoice_items)) {
                const normalizedItems = invoice.invoice_items.map(item => ({ ...item, tax_rate: normalizeTaxRate(item?.tax_rate), rate_per_unit: normalizeTaxRate(item?.rate_per_unit), no_of_units: normalizeTaxRate(item?.no_of_units), sub_total: normalizeTaxRate(item?.sub_total) }));
                const additionalTaxConfig = (invoice?.tax_details || []).map(t => ({ ...t, tax_percentage: t?.percentage }));
                const updatedInvoiceItems = getAdditionalTax(normalizedItems, additionalTaxConfig).map(({ rate_item, ...item }, idx) => ({
                    ...item,
                    sub_amount_total: (normalizedItems[idx]?.sub_amount_total && normalizedItems[idx]?.sub_total) ? normalizedItems[idx].sub_amount_total : item.sub_amount_total,
                }));
                const tax_details = getTotalTax(normalizedItems, additionalTaxConfig);
                payload = { ...payload, tax_details, invoice_items: updatedInvoiceItems };
            }
            globalPutService(updatePartiesApi(), payload)
                .then(async (response) => {
                    if (checkApiStatus(response)) {
                        getResponseBack()
                        onToggleModal()
                        trackActivity('Item Edited', { page_title: `Edit Invoice`, success_msg: response?.data?.message, item_id: invoice?.id, payload: invoice, response: response?.data })
                        enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        setLoading(false);
                    } else {
                        enqueueSnackbar(response?.data?.message, { variant: "error", anchorOrigin: { horizontal: "right", vertical: "top" }, });
                        setLoading(false);
                        trackActivity('Item Edited', { page_title: `Edit Invoice`, error_msg: response?.data?.message, item_id: invoice?.id, payload: invoice, response: response?.data })
                    }
                })
        } else {
            setError(validationInputs)
            trackActivity(`Item Edited`, { page_title: `Edit Invoice`, error_msg: validationInputs })
        }
    }

    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `Edit Invoice`, page_type: page_type, })
    }

    const showInvoiceItems = Array.isArray(invoice?.invoice_items) && !isManagementFeeInvoice(invoice);
    const isWideInvoiceType = [1, 2, 3].includes(invoice?.invoice_type?.value);
    const dialogWidthProps = showInvoiceItems
        ? getInvoiceDialogWidthProps({ invoice, type: getInvoiceItemsType(invoice), hideAddItem: true, activeTaxList: invoice?.tax_details }, isWideInvoiceType ? 'xl' : 'lg')
        : {
            maxWidth: isWideInvoiceType ? 'xl' : 'lg',
            fullWidth: true,
            sx: !invoice?.lease_slug && !isWideInvoiceType ? { '& .MuiDialog-paper': { maxWidth: titanAviationIntance ? '900px' : '1000px' } } : undefined,
        };

    return (
        <>
            <Dialog
                aria-labelledby="customized-dialog-title"
                className='add-lease-dialog'
                {...dialogWidthProps}
                open={true}
                scroll='paper'
            >
                <DialogTitle sx={{ m: 0, p: 2, pl: 3 }} className='dialog-header' id="customized-dialog-title">
                    <Typography className="delivery-header btn-primary" color="primary">
                        Edit Invoice Item's
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>

                        {Array.isArray(invoice?.invoice_items) && !isManagementFeeInvoice(invoice) ? (
                            <AInvItems
                                invoice={invoice}
                                type={getInvoiceItemsType(invoice)}
                                onFieldChange={onFieldChange}
                                onAddRemoveInvoiceItem={onAddRemoveInvoiceItem}
                                tableErrors={tableErrors}
                                resetTableError={resetTableError}
                                activeTaxList={invoice?.tax_details}
                                rateItems={rateItems}
                                rateItemsLoading={rateItemsLoading}
                                hideAddItem
                                disableItemField={[1, 2, 4, 5].includes(invoice?.invoice_type?.value) && Boolean(invoice?.lease_slug)}
                            />
                        ) : null}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ marginRight: "15px" }}>
                    <Button className='cancel-btn' variant='outlined' size='small' disabled={isLoading} onClick={() => { onToggleModal(); handleItemClicked('Cancel Button') }} > Cancel </Button>
                    <Button variant='contained' className='btn-primary' size='small' onClick={() => { handleItemClicked('Save Button'); onEditInvoice(); }}>
                        {isLoading ? <CircularProgress color='inherit' size={25} /> : "Save"}
                    </Button>
                </DialogActions>
            </Dialog>
            {isLoading ? <PageLoader /> : null}
        </>
    )
}

export default EditInvoiceItemModal